// src/components/common/Icon.tsx
import React from 'react';
import { IconName } from '../../types';

interface IconProps {
    name: IconName;
    size?: number;
    className?: string;
    strokeWidth?: number;
}

const paths: Record<IconName, React.ReactNode> = {
    grid: (
        <>
            <rect x="3" y="3" width="7" height="7" rx="1.6" />
            <rect x="14" y="3" width="7" height="7" rx="1.6" />
            <rect x="3" y="14" width="7" height="7" rx="1.6" />
            <rect x="14" y="14" width="7" height="7" rx="1.6" />
        </>
    ),
    briefcase: (
        <>
            <rect x="2.5" y="7.5" width="19" height="12.5" rx="2" />
            <path d="M8.5 7.5V5.6A1.6 1.6 0 0 1 10.1 4h3.8a1.6 1.6 0 0 1 1.6 1.6v1.9" />
            <path d="M2.5 12.5h19" />
            <path d="M10 12.5v1.8h4v-1.8" />
        </>
    ),
    home: (
        <>
            <path d="M3.5 10.2 12 3.5l8.5 6.7" />
            <path d="M5.6 11.8V20h12.8v-8.2" />
            <path d="M9.9 20v-5.2h4.2V20" />
        </>
    ),
    baby: (
        <>
            <circle cx="12" cy="8" r="4.6" />
            <path d="M10.2 7.6h.01M13.8 7.6h.01" />
            <path d="M10.4 10.2a2.6 2.6 0 0 0 3.2 0" />
            <path d="M6.6 20.5c.7-3.2 2.8-5 5.4-5s4.7 1.8 5.4 5" />
        </>
    ),
    book: (
        <>
            <path d="M4 4.5h5.2A2.8 2.8 0 0 1 12 7.3v12.2a2.2 2.2 0 0 0-2.2-2.2H4z" />
            <path d="M20 4.5h-5.2A2.8 2.8 0 0 0 12 7.3v12.2a2.2 2.2 0 0 1 2.2-2.2H20z" />
        </>
    ),
    rocket: (
        <>
            <path d="M13.6 4.8c3.4-1.6 6.1-1 6.1-1s.6 2.7-1 6.1c-1.4 3-3.9 5.2-6.1 6.4l-3.9-3.9c1.2-2.2 3.4-4.7 6.4-6.1z" />
            <circle cx="14.8" cy="9.2" r="1.5" />
            <path d="M8.7 12.4 6 13.2l-1.2 4.9 4.9-1.2.8-2.7" />
            <path d="M6.4 17.6 4.2 19.8" />
        </>
    ),
    heart: (
        <path d="M12 20.2s-7.4-4.4-7.4-9.4a4.2 4.2 0 0 1 7.4-2.7 4.2 4.2 0 0 1 7.4 2.7c0 5-7.4 9.4-7.4 9.4z" />
    ),
    search: (
        <>
            <circle cx="11" cy="11" r="6.6" />
            <path d="m20 20-3.6-3.6" />
        </>
    ),
    menu: <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" />,
    close: <path d="M6 6l12 12M18 6 6 18" />,
    chevronRight: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
    chevronDown: <path d="m5.5 9.5 6.5 6.5 6.5-6.5" />,
    arrowRight: (
        <>
            <path d="M4.5 12h15" />
            <path d="m13.5 6 6 6-6 6" />
        </>
    ),
    calendar: (
        <>
            <rect x="3.5" y="5" width="17" height="15.5" rx="2.2" />
            <path d="M3.5 10h17M8.5 3v4M15.5 3v4" />
        </>
    ),
    coin: (
        <>
            <circle cx="12" cy="12" r="8.5" />
            <path d="M12 7.2v9.6" />
            <path d="M14.6 9.4a2.7 2.7 0 0 0-2.6-1.6c-1.5 0-2.6.9-2.6 2.1s1 1.8 2.6 2.1 2.6.9 2.6 2.1-1.1 2.1-2.6 2.1a2.7 2.7 0 0 1-2.6-1.6" />
        </>
    ),
    users: (
        <>
            <circle cx="9" cy="8" r="3.4" />
            <path d="M2.8 20c0-3.4 2.8-5.6 6.2-5.6s6.2 2.2 6.2 5.6" />
            <path d="M16.2 5.1a3.4 3.4 0 0 1 0 6.4" />
            <path d="M17.6 14.8c2.2.6 3.6 2.5 3.6 5.2" />
        </>
    ),
    building: (
        <>
            <rect x="4" y="3.5" width="16" height="17" rx="1.8" />
            <path d="M8.2 8h2M14 8h2M8.2 12h2M14 12h2" />
            <path d="M10 20.5v-4h4v4" />
        </>
    ),
    check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
    chart: (
        <>
            <path d="M4 20V4" />
            <path d="M4 20h16" />
            <path d="M8 16.5V12M12.5 16.5V8M17 16.5v-6.5" />
        </>
    ),
    bell: (
        <>
            <path d="M18 8.8a6 6 0 1 0-12 0c0 6-2.2 7.4-2.2 7.4h16.4S18 14.8 18 8.8z" />
            <path d="M13.7 19.6a2 2 0 0 1-3.4 0" />
        </>
    ),
    sparkle: (
        <>
            <path d="M12 3.5 13.9 9l5.6 2-5.6 2-1.9 5.5L10.1 13 4.5 11l5.6-2z" />
            <path d="M18.5 3.5v3M20 5h-3" />
        </>
    ),
    document: (
        <>
            <path d="M13.5 3.5H7a1.8 1.8 0 0 0-1.8 1.8v13.4A1.8 1.8 0 0 0 7 20.5h10a1.8 1.8 0 0 0 1.8-1.8V8.8z" />
            <path d="M13.5 3.5v5.3h5.3" />
            <path d="M8.8 13h6.4M8.8 16.5h6.4" />
        </>
    ),
    external: (
        <>
            <path d="M13.5 4.5H20v6.5" />
            <path d="M20 4.5 11 13.5" />
            <path d="M18 14.5v4a1.8 1.8 0 0 1-1.8 1.8H5.8A1.8 1.8 0 0 1 4 18.5V8.2a1.8 1.8 0 0 1 1.8-1.8h4" />
        </>
    ),
    eye: (
        <>
            <path d="M2.2 12S5.6 5.8 12 5.8 21.8 12 21.8 12 18.4 18.2 12 18.2 2.2 12 2.2 12z" />
            <circle cx="12" cy="12" r="2.8" />
        </>
    ),
    clock: (
        <>
            <circle cx="12" cy="12" r="8.5" />
            <path d="M12 7v5.2l3.2 2" />
        </>
    ),
    shield: (
        <>
            <path d="M12 3.2 19 6v5.4c0 4.3-2.9 7.6-7 9.4-4.1-1.8-7-5.1-7-9.4V6z" />
            <path d="m9.2 12 2 2 3.6-3.8" />
        </>
    ),
    star: (
        <path d="m12 3.8 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 10l5.9-.9z" />
    ),
};

const Icon: React.FC<IconProps> = ({ name, size = 20, className, strokeWidth = 1.7 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
        focusable="false"
    >
        {paths[name]}
    </svg>
);

export default Icon;
